import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../userContext';
import { webhookService } from '../../services/webhookService';
import { notifySuccess, notifyError } from '../../utils/notify';
import { CheckCircleIcon, ClipboardIcon } from '@heroicons/react/24/outline';

// ─── Webhook Card ────────────────────────────────────────────────

function WebhookCard({ workspaceId }) {
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    webhookService.getConfig(workspaceId).then(config => {
      if (config) {
        setUrl(config.webhookUrl);
        setSecret(config.webhookSecret);
        setEnabled(config.webhookEnabled);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [workspaceId]);

  const handleSave = async () => {
    if (!workspaceId) return;
    const trimmedUrl = url.trim();
    if (enabled && !trimmedUrl) {
      notifyError('Enter a webhook URL before enabling');
      return;
    }
    if (trimmedUrl && !/^https?:\/\/.+/i.test(trimmedUrl)) {
      notifyError('Enter a valid URL starting with https://');
      return;
    }
    setSaving(true);
    try {
      await webhookService.saveConfig(workspaceId, {
        webhookUrl: trimmedUrl,
        webhookSecret: secret,
        webhookEnabled: enabled && !!trimmedUrl
      });
      setDirty(false);
      notifySuccess('Webhook settings saved');
    } catch (err) {
      notifyError('Failed to save webhook settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!url.trim()) {
      notifyError('Save a webhook URL first');
      return;
    }
    if (dirty) {
      notifyError('Save your changes before testing');
      return;
    }
    setTesting(true);
    try {
      const result = await webhookService.testWebhook();
      if (result.success) {
        notifySuccess('Test webhook delivered successfully');
      } else {
        notifyError(`Webhook test failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      notifyError('Failed to send test webhook');
    } finally {
      setTesting(false);
    }
  };

  const handleGenerateSecret = () => {
    setSecret(webhookService.generateSecret());
    setDirty(true);
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch {
      notifyError('Failed to copy');
    }
  };

  if (loading) {
    return (
      <div className="gch-bg-card border border-[var(--admin-border)] rounded-xl p-6 mb-6">
        <div className="h-6 w-32 rounded-lg animate-pulse bg-white/5 mb-4" />
        <div className="h-10 rounded-lg animate-pulse bg-white/5 mb-3" />
        <div className="h-10 rounded-lg animate-pulse bg-white/5" />
      </div>
    );
  }

  return (
    <div className="gch-bg-card border border-[var(--admin-border)] rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold gch-text-primary">Webhook</h3>
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
          enabled && url ? 'bg-green-500/10 text-green-400' : 'bg-white/5 gch-text-muted'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${enabled && url ? 'bg-green-400' : 'bg-white/20'}`} />
          {enabled && url ? 'Active' : 'Inactive'}
        </span>
      </div>
      <p className="text-sm gch-text-secondary mb-5">
        Send lead data to any URL when a player joins your leaderboard. Works with Zapier, Make, n8n, and custom endpoints.
      </p>

      {/* URL */}
      <div className="mb-4">
        <label className="block text-xs font-semibold gch-text-secondary uppercase tracking-wide mb-1.5">Endpoint URL</label>
        <input
          type="url"
          placeholder="https://hooks.zapier.com/hooks/catch/..."
          value={url}
          onChange={e => { setUrl(e.target.value); setDirty(true); }}
          className="w-full px-3 py-2.5 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none"
        />
      </div>

      {/* Secret */}
      <div className="mb-4">
        <label className="block text-xs font-semibold gch-text-secondary uppercase tracking-wide mb-1.5">Signing secret</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={secret}
              readOnly
              className="w-full px-3 py-2.5 pr-10 rounded-[var(--admin-card-radius)] gch-input-ghost border border-[var(--admin-border)] gch-text-muted text-sm font-mono text-xs"
            />
            {secret && (
              <button
                onClick={handleCopySecret}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 gch-text-muted hover:gch-text-primary transition-colors"
              >
                {secretCopied ? <CheckCircleIcon className="w-4 h-4 text-green-400" /> : <ClipboardIcon className="w-4 h-4" />}
              </button>
            )}
          </div>
          <button onClick={handleGenerateSecret} className="btn-admin-outline btn-admin-sm whitespace-nowrap">
            {secret ? 'Regenerate' : 'Generate'}
          </button>
        </div>
        <p className="text-xs gch-text-muted mt-1">Used to verify webhook payloads via HMAC-SHA256 signature in the <code className="text-xs">X-SmilesFlow-Signature</code> header.</p>
      </div>

      {/* Enable toggle */}
      <div className="flex items-center justify-between mb-5 py-2">
        <span className="text-sm font-medium gch-text-primary">Enable webhook</span>
        <button
          type="button"
          onClick={() => { setEnabled(!enabled); setDirty(true); }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            enabled ? 'bg-[var(--admin-accent)]' : 'bg-white/10'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving || !dirty} className="btn-admin-primary btn-admin-sm disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={handleTest} disabled={testing || !url.trim()} className="btn-admin-outline btn-admin-sm disabled:opacity-50">
          {testing ? 'Sending...' : 'Send test'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Panel ──────────────────────────────────────────────────

function AppsPanel() {
  // SaaS v1 (concern #14): every paid published game gets the full feature
  // set including integrations. The legacy "isSubscribed" gate has been
  // removed — webhook setup is available to anyone who reaches this page.
  const { businessProfile } = useUser();

  return (
    <div className="grow">
      <div className="p-6">
        <h2 className="text-2xl gch-text-primary font-bold mb-1">Integrations</h2>
        <p className="text-sm gch-text-secondary mb-6">
          Connect your tools to automatically sync lead data from your campaigns.
        </p>

        <WebhookCard workspaceId={businessProfile?.id} />
      </div>
    </div>
  );
}

export default AppsPanel;
